import { produce } from "immer";
import { LoyaltyActions } from "./loyaltyActions";
import { LoyaltyState } from "./loyaltyModels";
import {
  LOYALTY_ENROLL_REQUEST, LOYALTY_ENROLL_SUCCESS, LOYALTY_ENROLL_ERROR,
  LOYALTY_LOOKUP_REQUEST, LOYALTY_LOOKUP_SUCCESS, LOYALTY_LOOKUP_ERROR, LOYALTY_LOOKUP_CLEAR,
  LOYALTY_DASHBOARD_REQUEST, LOYALTY_DASHBOARD_SUCCESS, LOYALTY_DASHBOARD_ERROR,
  LOYALTY_PROGRAM_EXISTS_REQUEST, LOYALTY_PROGRAM_EXISTS_SUCCESS, LOYALTY_PROGRAM_EXISTS_ERROR,
  LOYALTY_AVAILABLE_REWARDS_REQUEST, LOYALTY_AVAILABLE_REWARDS_SUCCESS, LOYALTY_AVAILABLE_REWARDS_ERROR,
  LOYALTY_APPLY_REWARD_REQUEST, LOYALTY_APPLY_REWARD_SUCCESS, LOYALTY_APPLY_REWARD_ERROR,
  LOYALTY_SET_APPLIED_REDEMPTIONS,
  LOYALTY_REMOVE_REWARD_REQUEST, LOYALTY_REMOVE_REWARD_SUCCESS, LOYALTY_REMOVE_REWARD_ERROR,
  LOYALTY_POINTS_PREVIEW_REQUEST, LOYALTY_POINTS_PREVIEW_SUCCESS, LOYALTY_POINTS_PREVIEW_ERROR,
  LOYALTY_ORDER_IDENTIFY_REQUEST, LOYALTY_ORDER_IDENTIFY_SUCCESS, LOYALTY_ORDER_IDENTIFY_ERROR,
  LOYALTY_CHECKOUT_SUMMARY_REQUEST, LOYALTY_CHECKOUT_SUMMARY_SUCCESS, LOYALTY_CHECKOUT_SUMMARY_ERROR,
  LOYALTY_ORDER_COMPLETE_REQUEST, LOYALTY_ORDER_COMPLETE_SUCCESS, LOYALTY_ORDER_COMPLETE_ERROR,
  LOYALTY_ACCRUE_POINTS_REQUEST, LOYALTY_ACCRUE_POINTS_SUCCESS, LOYALTY_ACCRUE_POINTS_ERROR,
  LOYALTY_ACCRUE_IN_FLIGHT,
  LOYALTY_ACTIVE_PROMOS_SUCCESS, LOYALTY_ACTIVE_PROMOS_ERROR,
  LOYALTY_RECEIPT_DATA_REQUEST, LOYALTY_RECEIPT_DATA_SUCCESS, LOYALTY_RECEIPT_DATA_ERROR,
  LOYALTY_WALLET_PASS_GENERATE_REQUEST, LOYALTY_WALLET_PASS_GENERATE_SUCCESS, LOYALTY_WALLET_PASS_GENERATE_ERROR,
  LOYALTY_WALLET_PASS_GET_REQUEST, LOYALTY_WALLET_PASS_GET_SUCCESS, LOYALTY_WALLET_PASS_GET_ERROR,
  LOYALTY_RESET,
  LOYALTY_INVALIDATE_CACHE,
  LOYALTY_SET_SKIP_FOR_ORDER,
  LOYALTY_CUSTOMER_TRANSACTIONS_REQUEST, LOYALTY_CUSTOMER_TRANSACTIONS_SUCCESS, LOYALTY_CUSTOMER_TRANSACTIONS_ERROR,
  LOYALTY_SET_POINTS_TO_REDEEM,
} from "./loyaltyConstants";

const initialState: LoyaltyState = {
  isEnrolled: false,
  enrollLoading: false,
  enrollError: false,

  programExists: {
    brand_has_program: false,
    location_has_program: false,
    program_status: null,
    programExistsLoading: false,
    programExistsFetched: false,
  },

  lookupResult: null,
  lookupLoading: false,
  lookupFetched: false,

  dashboard: null,
  dashboardLoading: false,
  dashboardFetched: false,

  availableRewards: null,
  availableRewardsLoading: false,

  appliedReward: null,
  appliedRedemptions: [],
  applyRewardLoading: false,
  applyRewardError: null,

  pointsPreview: null,
  pointsPreviewLoading: false,

  checkoutSummary: null,
  checkoutSummaryLoading: false,

  orderCompleteResult: null,
  orderCompleteLoading: false,

  accrualResult: null,
  accrualLoading: false,
  accrualError: null,
  accrualInFlight: false,

  activePromotions: [],
  activePromotionsLoading: false,
  activePromotionsFetched: false,
  loyaltyProgramNotFound: false,

  receiptData: null,
  receiptDataError: false,

  walletPass: null,
  walletPassLoading: false,

  skipLoyaltyForOrder: false,

  customerTransactions: null,
  customerTransactionsLoading: false,
  customerTransactionsFetched: false,
  customerTransactionsPointsName: 'Points',

  pointsToRedeem: null,
};

export default function loyaltyReducer(state: LoyaltyState = initialState, action: LoyaltyActions) {
  return produce(state, (draft) => {
    switch (action.type) {
      // Enrollment
      case LOYALTY_ENROLL_REQUEST:
        draft.enrollLoading = true;
        draft.enrollError = false;
        break;
      case LOYALTY_ENROLL_SUCCESS:
        draft.enrollLoading = false;
        draft.isEnrolled = true;
        draft.lookupResult = action.payload as any;
        break;
      case LOYALTY_ENROLL_ERROR:
        draft.enrollLoading = false;
        draft.enrollError = true;
        break;

      // Lookup
      case LOYALTY_LOOKUP_REQUEST:
        draft.lookupLoading = true;
        break;
      case LOYALTY_LOOKUP_SUCCESS:
        draft.lookupLoading = false;
        draft.lookupResult = action.payload;
        draft.isEnrolled = true;
        draft.lookupFetched = true;
        break;
      case LOYALTY_LOOKUP_ERROR:
        draft.lookupLoading = false;
        draft.isEnrolled = false;
        draft.lookupFetched = true;
        break;
      case LOYALTY_LOOKUP_CLEAR:
        draft.lookupResult = null;
        draft.isEnrolled = false;
        draft.lookupFetched = false;
        break;

      // Dashboard
      case LOYALTY_DASHBOARD_REQUEST:
        draft.dashboardLoading = true;
        break;
      case LOYALTY_DASHBOARD_SUCCESS:
        draft.dashboardLoading = false;
        draft.dashboardFetched = true;
        draft.dashboard = action.payload;
        break;
      case LOYALTY_DASHBOARD_ERROR:
        draft.dashboardLoading = false;
        draft.dashboardFetched = true;
        break;

      // Program Exists by Location
      case LOYALTY_PROGRAM_EXISTS_REQUEST:
        draft.programExists.programExistsLoading = true;
        break;
      case LOYALTY_PROGRAM_EXISTS_SUCCESS:
        draft.programExists.programExistsLoading = false;
        draft.programExists.programExistsFetched = true;
        draft.programExists.brand_has_program = Boolean((action.payload as any)?.brand_has_program);
        draft.programExists.location_has_program = Boolean((action.payload as any)?.location_has_program);
        draft.programExists.program_status = (action.payload as any)?.program_status ?? null;
        break;
      case LOYALTY_PROGRAM_EXISTS_ERROR:
        draft.programExists.programExistsLoading = false;
        draft.programExists.programExistsFetched = true;
        draft.programExists.brand_has_program = false;
        draft.programExists.location_has_program = false;
        draft.programExists.program_status = null;
        break;

      // Available Rewards
      case LOYALTY_AVAILABLE_REWARDS_REQUEST:
        draft.availableRewardsLoading = true;
        break;
      case LOYALTY_AVAILABLE_REWARDS_SUCCESS:
        draft.availableRewardsLoading = false;
        draft.availableRewards = action.payload;
        break;
      case LOYALTY_AVAILABLE_REWARDS_ERROR:
        draft.availableRewardsLoading = false;
        break;

      // Apply Reward
      case LOYALTY_APPLY_REWARD_REQUEST:
        draft.applyRewardLoading = true;
        draft.applyRewardError = null;
        break;
      case LOYALTY_APPLY_REWARD_SUCCESS:
        draft.applyRewardLoading = false;
        draft.appliedReward = action.payload;
        break;
      case LOYALTY_APPLY_REWARD_ERROR:
        draft.applyRewardLoading = false;
        draft.applyRewardError = action.payload;
        break;

      // Remove Reward
      case LOYALTY_REMOVE_REWARD_REQUEST:
        draft.applyRewardLoading = true;
        break;
      case LOYALTY_REMOVE_REWARD_SUCCESS:
        draft.applyRewardLoading = false;
        draft.appliedReward = null;
        draft.appliedRedemptions = [];
        draft.pointsToRedeem = null;
        break;
      case LOYALTY_REMOVE_REWARD_ERROR:
        draft.applyRewardLoading = false;
        break;

      // Bulk-applied redemptions list (populated by applyRewardSaga from single + bulk responses)
      case LOYALTY_SET_APPLIED_REDEMPTIONS:
        draft.appliedRedemptions = action.payload;
        break;

      // Points Preview
      case LOYALTY_POINTS_PREVIEW_REQUEST:
        draft.pointsPreviewLoading = true;
        break;
      case LOYALTY_POINTS_PREVIEW_SUCCESS:
        draft.pointsPreviewLoading = false;
        draft.pointsPreview = action.payload;
        break;
      case LOYALTY_POINTS_PREVIEW_ERROR:
        draft.pointsPreviewLoading = false;
        break;

      // Order Identify
      case LOYALTY_ORDER_IDENTIFY_REQUEST:
      case LOYALTY_ORDER_IDENTIFY_SUCCESS:
      case LOYALTY_ORDER_IDENTIFY_ERROR:
        break;

      // Checkout Summary
      case LOYALTY_CHECKOUT_SUMMARY_REQUEST:
        draft.checkoutSummaryLoading = true;
        break;
      case LOYALTY_CHECKOUT_SUMMARY_SUCCESS:
        draft.checkoutSummaryLoading = false;
        draft.checkoutSummary = action.payload;
        break;
      case LOYALTY_CHECKOUT_SUMMARY_ERROR:
        draft.checkoutSummaryLoading = false;
        break;

      // Order Complete
      case LOYALTY_ORDER_COMPLETE_REQUEST:
        draft.orderCompleteLoading = true;
        break;
      case LOYALTY_ORDER_COMPLETE_SUCCESS:
        draft.orderCompleteLoading = false;
        draft.orderCompleteResult = action.payload;
        break;
      case LOYALTY_ORDER_COMPLETE_ERROR:
        draft.orderCompleteLoading = false;
        break;

      // Accrue Points (post-payment)
      case LOYALTY_ACCRUE_IN_FLIGHT:
        draft.accrualInFlight = true;
        draft.accrualError = null;
        break;
      case LOYALTY_ACCRUE_POINTS_REQUEST:
        draft.accrualLoading = true;
        draft.accrualError = null;
        break;
      case LOYALTY_ACCRUE_POINTS_SUCCESS:
        draft.accrualLoading = false;
        draft.accrualInFlight = false;
        draft.accrualResult = action.payload;
        break;
      case LOYALTY_ACCRUE_POINTS_ERROR:
        draft.accrualLoading = false;
        draft.accrualInFlight = false;
        draft.accrualError = action.payload;
        break;

      // Active Promotions
      case LOYALTY_ACTIVE_PROMOS_SUCCESS:
        draft.activePromotionsLoading = false;
        draft.activePromotionsFetched = true;
        draft.activePromotions = action.payload;
        break;
      case LOYALTY_ACTIVE_PROMOS_ERROR:
        draft.activePromotionsLoading = false;
        draft.activePromotionsFetched = true;
        draft.loyaltyProgramNotFound = action.payload === true;
        break;

      // Receipt Data
      case LOYALTY_RECEIPT_DATA_REQUEST:
        draft.receiptDataError = false;
        break;
      case LOYALTY_RECEIPT_DATA_SUCCESS:
        draft.receiptData = action.payload;
        draft.receiptDataError = false;
        break;
      case LOYALTY_RECEIPT_DATA_ERROR:
        draft.receiptDataError = true;
        break;

      // Wallet Pass Generate
      case LOYALTY_WALLET_PASS_GENERATE_REQUEST:
        draft.walletPassLoading = true;
        break;
      case LOYALTY_WALLET_PASS_GENERATE_SUCCESS:
        draft.walletPassLoading = false;
        draft.walletPass = action.payload;
        break;
      case LOYALTY_WALLET_PASS_GENERATE_ERROR:
        draft.walletPassLoading = false;
        break;

      // Wallet Pass Get
      case LOYALTY_WALLET_PASS_GET_REQUEST:
        draft.walletPassLoading = true;
        break;
      case LOYALTY_WALLET_PASS_GET_SUCCESS:
        draft.walletPassLoading = false;
        draft.walletPass = action.payload;
        break;
      case LOYALTY_WALLET_PASS_GET_ERROR:
        draft.walletPassLoading = false;
        break;

      // Customer Transactions (full history)
      case LOYALTY_CUSTOMER_TRANSACTIONS_REQUEST:
        draft.customerTransactionsLoading = true;
        break;
      case LOYALTY_CUSTOMER_TRANSACTIONS_SUCCESS:
        draft.customerTransactionsLoading = false;
        draft.customerTransactionsFetched = true;
        draft.customerTransactions = (action.payload as any).transactions;
        draft.customerTransactionsPointsName = (action.payload as any).pointsName || 'Points';
        break;
      case LOYALTY_CUSTOMER_TRANSACTIONS_ERROR:
        draft.customerTransactionsLoading = false;
        draft.customerTransactionsFetched = true;
        break;

      // Invalidate cache — clear fetched flags + stale data, keep auth state
      case LOYALTY_INVALIDATE_CACHE:
        draft.dashboard = null;
        draft.dashboardFetched = false;
        draft.availableRewards = null;
        draft.activePromotionsFetched = false;
        draft.customerTransactions = null;
        draft.customerTransactionsFetched = false;
        draft.customerTransactionsPointsName = 'Points';
        break;

      // Reset
      case LOYALTY_RESET:
        return initialState;

      // Pay-at-Store / Pay-on-Delivery skip flag
      case LOYALTY_SET_SKIP_FOR_ORDER:
        draft.skipLoyaltyForOrder = action.payload;
        break;

      // Points to redeem (capped to order total for value_per_points rewards)
      case LOYALTY_SET_POINTS_TO_REDEEM:
        draft.pointsToRedeem = action.payload;
        break;
    }
  });
}
